import type { KeyboardEvent } from "react";
import { ArrowRight, ExternalLink, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import type { CapabilityStatus } from "@/entities/capability";

/**
 * 首页横滑带的**紧凑统一卡**(2026-07-25 发现流)——发现层专用。
 *
 * ★与群页富卡分工:首页每群一行横滑,dept 舱与能力舱**统一成同一种紧凑卡**(icon + 名 + 一句话 +
 *   三态 + CTA),行高一致、一眼扫全貌;富部门卡(主角图/积分标/hover 真样图)只在**群页**出现。
 *   这样首页不再是 6000px 长墙,也不会出现被撑大的巨型空卡(舞台固定矮,不用 aspect 1/0.82)。
 * ★整卡可点必带 role=button + tabIndex + Enter/Space(门 H-D 三件套);占位态不可点、aria-disabled。
 */
interface Props {
  dataKey: string;
  name: string;
  desc: string;
  icon: LucideIcon;
  status: CapabilityStatus;
  /** stage 上的形制小标(可选)。 */
  kind?: string;
  /** 已开可点(占位不传)。 */
  onOpen?: () => void;
  /**
   * 外链舱(点了会新开标签页去外站,如漫剧工坊)。
   * ★首页这条带是**紧凑统一卡**,不给外链舱另做一种卡 —— 但「点了会离开本站」这件事
   *   必须说:kind 标旁挂一枚「独立站 ↗」、CTA 换成「前往独立站 ↗」、aria-label 讲明
   *   在新标签页打开。与群页富卡是同一套说法,两处不打架(0920 统一批)。
   */
  external?: boolean;
  /**
   * 右侧人物图 URL(dept 舱传 deptFigureOf、能力舱传 capFigureOf;由页面层算,本卡不感知皮肤)。
   * 404 静默降级:background-image 什么都不显示,icon 舞台照常(figures.ts 同款判据)。
   */
  fig?: string;
  /** 1005:因套餐到期关闭的舱 —— 照样上墙,标「已到期」、不可点(占位态同「即将」)。 */
  expired?: boolean;
}

const STATUS_PILL: Record<CapabilityStatus, string> = {
  active: "text-success bg-success/15",
  integrating: "text-warning bg-warning/15",
  coming_soon: "text-muted-foreground bg-muted",
};

export function ClusterRowCard({
  dataKey,
  name,
  desc,
  icon: Icon,
  status,
  kind,
  onOpen,
  fig,
  external,
  expired = false,
}: Props) {
  const { t } = useTranslation();
  const active = status === "active" && !expired;
  const clickable = active && !!onOpen;
  const statusKey = status === "coming_soon" ? "coming" : status;
  const ctaLabel = expired
    ? "套餐已到期"
    : active
      ? t(external ? "cabin.cta.openExternal" : "cabin.cta.enter")
      : t(`cabin.cta.${statusKey}`);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.();
    }
  };

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable && external ? t("cabin.external.openAria", { name }) : undefined}
      aria-disabled={clickable ? undefined : true}
      onClick={clickable ? onOpen : undefined}
      onKeyDown={clickable ? onKeyDown : undefined}
      data-row-card={dataKey}
      data-status={expired ? "expired" : status}
      className={cn(
        "scene-cap group relative flex w-full flex-col overflow-hidden border bg-card text-left",
        CARD_WALL_RADIUS,
        clickable ? "cursor-pointer" : "cursor-default"
      )}
    >
      {/* 右侧人物(挂小人,2026-07-27):素材底色=皮肤 --card 真值,向左渐隐融进卡底;
          占位舱人物同步降饱和(与舞台同一对 token,不出现"卡灰人亮")。 */}
      {fig && (
        <span
          className={cn("scene-litefig", !active && "opacity-70 saturate-[.55]")}
          style={{ backgroundImage: `url(${fig})` }}
          aria-hidden
        />
      )}
      {/* 矮舞台:只放 icon,固定高度(不用 scene-stage 的 aspect,避免宽卡被撑成巨盒)
          ★有人物时舞台切 bg-card:素材四周烘的就是 --card 真值,bg-muted 会露素材底硬边;
          icon 让位到左侧非人物区居中。 */}
      <div
        className={cn(
          "relative grid h-[76px] w-full place-items-center",
          fig ? "bg-card pr-[38%]" : "bg-muted",
          !active && "opacity-70 saturate-[.55]"
        )}
      >
        <Icon className="h-8 w-8 text-primary/90" strokeWidth={1.5} aria-hidden />
        {(kind || external) && (
          <span className="absolute bottom-2 left-2 z-20 flex items-center gap-1">
            {kind && (
              <span className="rounded-full bg-card/85 px-2 py-0.5 text-2xs leading-none text-card-foreground backdrop-blur-sm">
                {kind}
              </span>
            )}
            {external && (
              <span className="inline-flex items-center gap-0.5 rounded-full border border-primary/45 bg-card/85 px-1.5 py-0.5 text-2xs leading-none text-card-foreground backdrop-blur-sm">
                <ExternalLink className="h-2.5 w-2.5" aria-hidden />
                {t("cabin.external.badge")}
              </span>
            )}
          </span>
        )}
      </div>

      <div className="relative z-20 flex flex-1 flex-col gap-1 p-3">
        <div className={cn("flex items-center gap-2", fig && "pr-[26%]")}>
          <h3
            className={cn(
              "text-sm font-[650] tracking-[-0.012em]",
              !active && "text-muted-foreground"
            )}
          >
            {name}
          </h3>
          <span
            className={cn(
              "ml-auto inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-2xs font-semibold",
              expired ? "text-destructive bg-destructive/10" : STATUS_PILL[status]
            )}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {expired ? "已到期" : t(`cabin.status.${statusKey}`)}
          </span>
        </div>
        <p
          className={cn(
            "line-clamp-2 text-2xs leading-[1.6] text-muted-foreground",
            fig && "pr-[26%]"
          )}
        >
          {desc}
        </p>
        <span
          className={cn(
            "mt-auto inline-flex items-center gap-0.5 pt-1.5 text-2xs font-semibold",
            active ? "text-primary" : "font-medium text-muted-foreground"
          )}
        >
          {ctaLabel}
          {active &&
            (external ? <ExternalLink className="h-3 w-3" /> : <ArrowRight className="h-3 w-3" />)}
        </span>
      </div>
    </div>
  );
}
